/**
 * Action previews: hover/inspect a Keep/Combine/Recipe button to see the
 * outcome on the board. Pure functions over the Game -- ported from the
 * prototype's previewKeep/previewCombine/previewRecipe, including the exact
 * consume-selection order (which mirrors what the action would consume).
 */
import { RECIPES } from '../data/balance.js';
import type { Game } from '../core/game.js';
import type { GemCell } from '../core/types.js';
import type { ActionPreview } from '../render/viewState.js';
import type { SlateKey, SpecialKey } from '../data/balance.js';

function build(game: Game, sel: GemCell, consumed: readonly GemCell[]): ActionPreview {
  return {
    result: { x: sel.x, y: sel.y },
    consume: consumed.map((g) => ({ x: g.x, y: g.y })),
    dim: game.newGems
      .filter((g) => g !== sel && !consumed.includes(g))
      .map((g) => ({ x: g.x, y: g.y })),
  };
}

export function previewKeep(game: Game, i: number): ActionPreview | null {
  const sel = game.newGems[i];
  return sel ? build(game, sel, []) : null;
}

export function previewCombine(game: Game, i: number, need: number): ActionPreview | null {
  const sel = game.newGems[i];
  if (!sel) return null;
  const others = game
    .identicalIn(game.combinePool(), sel)
    .filter((g) => g !== sel)
    .sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0))
    .slice(0, need - 1);
  return build(game, sel, others);
}

/** Slate forge: every component is consumed; the result lands in a rock. */
export function previewSlate(game: Game, i: number, key: SlateKey): ActionPreview | null {
  const sel = game.newGems[i];
  if (!sel) return null;
  const comps = game.slateComponentsFor(sel, key);
  if (!comps) return null;
  return {
    result: null,
    consume: comps.map((g) => ({ x: g.x, y: g.y })),
    dim: game.newGems.filter((g) => !comps.includes(g)).map((g) => ({ x: g.x, y: g.y })),
  };
}

export function previewRecipe(game: Game, i: number, key: SpecialKey): ActionPreview | null {
  const sel = game.newGems[i];
  const recipe = RECIPES.find((r) => r.key === key);
  if (!sel || !recipe) return null;
  const pool = game.combinePool();
  let selfUsed = false;
  const toConsume: GemCell[] = [];
  for (const [type, tier] of recipe.comps) {
    if (!selfUsed && sel.type === type && sel.tier === tier) {
      selfUsed = true;
      continue;
    }
    const g = pool.find(
      (p) => p.type === type && p.tier === tier && p !== sel && !toConsume.includes(p),
    );
    if (g) toConsume.push(g);
  }
  return build(game, sel, toConsume);
}
